import { useCallback, useState } from 'react';

import { LocationNodeData } from '../../../@types';
import { SearchCode } from '../../../@types/part.type';
import {
  useSearchNodeByParentId,
  useSearchNodeWithParts,
  useSubLocationsForCreateServices,
} from '../../../queries/live-search-query';
import { getHasCodeForNodeSearch } from '../../../utils/services';

export interface LocationSearchState {
  mainLocationNode?: LocationNodeData;
  subLocationNode?: LocationNodeData;
  subLocationOptions?: LocationNodeData[];
  locationText: string;
  locationId?: number;
  searchError: boolean;
}

const EMPTY_STATE: LocationSearchState = {
  mainLocationNode: undefined,
  subLocationNode: undefined,
  subLocationOptions: undefined,
  locationText: '',
  locationId: undefined,
  searchError: false,
};

export interface UseLocationSearchResult {
  state: LocationSearchState;
  search: (name: string, parentId: string, clientId?: number) => Promise<LocationNodeData[]>;
  onSelect: (node: LocationNodeData) => Promise<void>;
  onSelectSubLocation: (node: LocationNodeData) => void;
  clear: () => void;
  dismissOptions: () => void;
}

export const useLocationSearch = (): UseLocationSearchResult => {
  const [state, setState] = useState<LocationSearchState>(EMPTY_STATE);

  const searchNodeByParentId = useSearchNodeByParentId();
  const searchNodeWithParts = useSearchNodeWithParts();
  const subLocationsQuery = useSubLocationsForCreateServices();

  const applySubLocations = useCallback(
    (subLocationsResult: LocationNodeData[] | undefined, fallbackNode: LocationNodeData) => {
      if (!Array.isArray(subLocationsResult) || subLocationsResult.length === 0) {
        setState((prev) => ({
          ...prev,
          mainLocationNode: fallbackNode,
          subLocationNode: undefined,
          subLocationOptions: undefined,
          locationText: fallbackNode.name,
          searchError: false,
        }));
        return;
      }
      const main = subLocationsResult.find((n) => n.isMain);
      const sub = subLocationsResult.find((n) => !n.isMain && n.isSelected);
      const onlySubs = subLocationsResult.filter((n) => !n.isMain);
      if (main && sub) {
        setState((prev) => ({
          ...prev,
          mainLocationNode: main,
          subLocationNode: sub,
          locationText: `${main.name} / ${sub.name}`,
          subLocationOptions: undefined,
          searchError: false,
        }));
        return;
      }
      if (main?.isSelected && onlySubs.length === 0) {
        setState((prev) => ({
          ...prev,
          mainLocationNode: main,
          subLocationNode: undefined,
          locationText: main.name,
          subLocationOptions: undefined,
          searchError: false,
        }));
        return;
      }
      setState((prev) => ({
        ...prev,
        subLocationOptions: subLocationsResult,
        searchError: false,
      }));
    },
    []
  );

  const search = useCallback(
    async (name: string, parentId: string, clientId?: number) => {
      const hasCode = getHasCodeForNodeSearch(name);
      if (name.toLowerCase().startsWith(SearchCode.S_HASH)) {
        return await searchNodeWithParts.mutateAsync({
          name: name.replace('#', '%23'),
          isInClientHierarchy: !!clientId,
          clientId: clientId ?? null,
        });
      }
      return await searchNodeByParentId.mutateAsync({
        selectedParentId: parentId || undefined,
        name,
        hasCode,
      });
    },
    [searchNodeByParentId, searchNodeWithParts]
  );

  const onSelect = useCallback(
    async (node: LocationNodeData) => {
      const id = node?.isPartSearch ? node.locationId : node?.id;
      if (!id || id === 0) {
        setState((prev) => ({ ...prev, searchError: true }));
        return;
      }
      setState((prev) => ({ ...prev, locationId: id }));
      let subResults: LocationNodeData[] | undefined;
      try {
        subResults = await subLocationsQuery.mutateAsync(id);
      } catch {
        subResults = undefined;
      }
      applySubLocations(subResults, node);
    },
    [subLocationsQuery, applySubLocations]
  );

  const onSelectSubLocation = useCallback((node: LocationNodeData) => {
    setState((prev) => {
      if (!prev.subLocationOptions) return prev;
      const main = prev.subLocationOptions.find((i) => i.isMain);
      const sub = prev.subLocationOptions.find((i) => !i.isMain && i.id === node.id);
      return {
        ...prev,
        mainLocationNode: main,
        subLocationNode: sub,
        locationText: `${main?.name || ''} / ${sub?.name || ''}`,
        subLocationOptions: undefined,
        searchError: false,
      };
    });
  }, []);

  const dismissOptions = useCallback(() => {
    setState((prev) => ({ ...prev, subLocationOptions: undefined }));
  }, []);

  const clear = useCallback(() => {
    setState(EMPTY_STATE);
  }, []);

  return { state, search, onSelect, onSelectSubLocation, clear, dismissOptions };
};
